<template>
    <span>
        <!-- Selected items UI -->
        <q-btn
            :disable="!isPhaseEditable(phaseEnd)"
            @click="openCausesDialog"
            :icon="
                !hasIconRight && !hideIcon
                    ? 'far fa-exclamation-circle'
                    : undefined
            "
            :icon-right="iconRight"
            :color="getButtonColor"
            :flat="isFlat"
            :outline="isOutlined"
            :label="showLabel ? $t('Add cause') : ''"
            :dense="isDense"
        >
            <q-tooltip>
                {{
                    !isPhaseEditable(phaseEnd)
                        ? getUneditableErrorMessage
                        : $t('Click here to add a cause')
                }}
            </q-tooltip>
        </q-btn>
        <!-- Dialog -->
        <q-dialog v-model="showDialog">
            <q-card
                class="dialog"
                :class="$q.screen.gt.lg ? 'dialog__lg' : 'dialog__xs'"
            >
                <!-- Dialog header -->
                <q-bar class="bg-primary">
                    <div class="text-white">
                        {{ dialogTitle }}
                    </div>

                    <q-space />

                    <q-btn
                        :disable="isLoading"
                        @click="closeDialog"
                        class="text-white"
                        icon="close"
                        dense
                        flat
                    >
                        <q-tooltip>{{ $t('Close') }}</q-tooltip>
                    </q-btn>
                </q-bar>

                <!-- Table -->
                <q-card-section>
                    <q-input
                        v-model="search"
                        @clear="search = ''"
                        :placeholder="searchLabel"
                        color="accent"
                        class="q-mb-sm"
                        debounce="500"
                        clearable
                        outlined
                        dense
                        style="width: 100%"
                    />
                    <q-table
                        @request="onRequest"
                        :rows="causeList"
                        :columns="columns"
                        :filter="search"
                        v-model:pagination="pagination"
                        :rows-per-page-options="[30]"
                        v-model:selected="selected"
                        :loading="isLoading"
                        :row-key="rowKey"
                        :no-data-label="noDataLabel || $t('No data available')"
                        :grid="cardMode"
                        table-class="sticky-table table-mode"
                        card-container-class="card-mode"
                        selection="single"
                        binary-state-sort
                        bordered
                        flat
                    >
                        <!-- Card Mode -->
                        <template v-slot:item="props">
                            <slot name="item" :props="props"></slot>
                        </template>
                    </q-table>
                </q-card-section>

                <!-- Dialog actions -->
                <q-card-section
                    v-if="!loadingOptionsFailed"
                    class="q-px-lg q-pb-md float-right"
                >
                    <q-btn
                        @click="closeDialog"
                        :disable="isLoading"
                        class="q-mr-md"
                        color="white"
                        text-color="primary"
                        outline
                        >{{ $t('Cancel') }}</q-btn
                    >
                    <q-btn
                        :disable="isLoading || $userCannot('edit', 'causes')"
                        :label="$t('Save')"
                        @click="confirm"
                        color="primary"
                        :loading="isLoading"
                    />
                </q-card-section>
            </q-card>
        </q-dialog>
    </span>
</template>

<script>
import TableSelectMixin from '@mixins/TableSelectMixin.vue';
import UpdateURL from '@mixins/UpdateURL.vue';
import {
    isPhaseEditable,
    isPhaseInProgress,
    DEFAULT_EDITABLE_PHASE_DURATION_DAYS
} from '@utils/phaseChecker.js';
import { useNotificationsStore } from '@stores/notifications.ts';
import { mapActions } from 'pinia';

export default {
    name: 'MissingCauseLabel',
    mixins: [TableSelectMixin, UpdateURL],
    emits: ['refreshList', 'clearSelection', 'causeJustified'],
    props: {
        phaseId: {
            type: [Number, String],
            require: true
        },
        phaseUUID: {
            type: String,
            required: true
        },
        phaseEnd: {
            type: String,
            required: false
        },
        baseURL: {
            type: String,
            required: true
        },
        queryParams: {
            type: Object,
            required: false
        },
        showLabel: {
            type: Boolean,
            default: true
        },
        buttonColor: {
            type: String,
            required: false
        },
        isFlat: {
            type: Boolean,
            default: false
        },
        isOutlined: {
            type: Boolean,
            default: false
        },
        isDense: {
            type: Boolean,
            default: false
        },
        iconRight: {
            type: String,
            required: false
        },
        hideIcon: {
            type: Boolean,
            required: false
        }
    },
    data() {
        return {
            DEFAULT_EDITABLE_PHASE_DURATION_DAYS,
            isLoading: false,
            pagination: {
                sortBy: 'start',
                descending: true,
                page: 1,
                rowsPerPage: 30,
                rowsNumber: null
            },
            loadingOptionsFailed: false,
            causeList: []
        };
    },
    watch: {
        /**
         * Updates the list of causes when the user types
         * on the search bar
         */
        search() {
            this.getCauses();
        }
    },
    computed: {
        /**
         * Get the selected cause id from the 'value' mixin prop
         * @return {Number}
         */
        selectedCause() {
            if (this.modelValue) {
                return this.modelValue.value;
            }
            return null;
        },
        /**
         * Show phase is uneditable error message
         * @return {String} - Uneditable message
         */
        getUneditableErrorMessage() {
            return this.$t(
                "Phases that have finished more than {editablePeriod} days ago can't be edited.",
                {
                    editablePeriod: parseInt(
                        process.env.EDITABLE_PHASE_DURATION_DAYS ||
                            this.DEFAULT_EDITABLE_PHASE_DURATION_DAYS
                    )
                }
            );
        },
        /**
         * Payload to send server.
         * @return {Object}
         */
        payload() {
            const payload = {
                uuid: this.phaseUUID,
                cause_id: null
            };
            // check if values has been selected
            if (this.selectedCause) {
                payload['cause_id'] = this.selectedCause;
            }

            return payload;
        },
        /**
         * Get button color based on phase editable status.
         * @return {String}
         */
        getButtonColor() {
            if (this.buttonColor) {
                return this.buttonColor;
            }
            if (this.isPhaseEditable(this.phaseEnd)) {
                return 'negative';
            }
            return 'grey-7';
        },
        /**
         * Check if icon right is set.
         * @return {Boolean}
         */
        hasIconRight() {
            return Boolean(this.iconRight);
        }
    },
    methods: {
        isPhaseEditable,
        isPhaseInProgress,
        ...mapActions(useNotificationsStore, ['resolveNotificationAction']),
        /**
         * Overrides onRequest method (mixin) to avoid multiple API calls
         */
        async onRequest() {
            this.pagination['rowsNumber'] = this.causeList.count;
        },
        /**
         * Opens causes dialog and load the list of causes for this
         */
        openCausesDialog() {
            this.showDialog = true;
            this.getCauses();
        },
        /**
         * Get list of causes and update the list based on the search.
         */
        async getCauses() {
            this.isLoading = true;
            this.$axios
                .get(this.url)
                .then(response => {
                    // empty the current list of causes
                    this.causeList = [];
                    // push the new list of causes
                    this.causeList.push(...response.data.results);
                    // update total items count
                    this.pagination.rowsNumber = response.data.count;
                    // toggle loading options in the case that there was previously an error
                    this.loadingOptionsFailed = false;
                })
                .catch(() => {
                    this.loadingOptionsFailed = true;
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        /**
         * Get causes filtered by search params.
         */
        async confirm() {
            // Check if a cause was selected
            if (this.selectedCause === null) {
                return this.$notifyError(
                    this.$t('Failed to update. Please select a cause.')
                );
            }

            // Disable submit button and start loading animation
            this.isLoading = true;

            const errorMessage = this.$t(
                'Failed to update cause at this time. Please try again later.'
            );
            // Update the phase with the selected cause
            await this.$axios
                .post(`events/resolve/`, this.payload)
                .then(response => {
                    // notify user
                    this.$notifySuccess(
                        this.$t('Cause has been successfully updated')
                    );
                    // Request updated data for the parent
                    this.$emit('refreshList');
                    // Confirm that a cause was justified to prevent unnecessary requests
                    this.$emit('causeJustified');
                })
                .catch(error => {
                    if (
                        error &&
                        typeof error == 'object' &&
                        'uneditable_phase' in error
                    ) {
                        this.$notifyError(this.getUneditableErrorMessage);
                    } else {
                        this.$notifyError(errorMessage, error);
                    }
                });

            // remove notification
            this.resolveNotificationAction(this.phaseUUID);
            // Enable submit button
            this.isLoading = false;

            this.closeDialog();
        },
        /**
         * Clear the selected cause and close the dialog using TableSelectMixin method
         */
        closeDialog() {
            // Next time the dialog opens the search input will be empty
            this.search = '';
            this.$emit('clearSelection');
            this.close();
        }
    }
};
</script>

<style lang="scss" scoped>
.dialog__lg {
    width: 50%;
    max-width: none;
}
.dialog__xs {
    width: 100%;
    max-width: none;
}
// scroll area for the cards container of the component
:deep(.card-mode) {
    max-height: 60vh;
    overflow-y: scroll;
}
// scroll area for the table rows part of the component
:deep(.table-mode) {
    height: 55vh;
}
</style>
